import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getProduttore, updateProduttore, geocodeSingle, deleteProduttore } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, MapPin, Map, Save, Trash2, Loader2, Clock } from "lucide-react";
import { FREQUENZE, FREQUENZE_LABEL, ALTERNANZE, SETTIMANE_MESE, MESI_ALTERNANZA, RINNOVI, GIORNI, FASCE_VISITA, FASCE_VISITA_LABEL, formatDateIt, daysUntil, classifyScadenza, SCADENZA_LABEL, SCADENZA_COLOR } from "../lib/constants";
import { useZoneAttive } from "../hooks/useZone";
import { getScadenze } from "../lib/api";
import { toast } from "sonner";

function Section({ title, children }) {
  return (
    <Card className="p-5 bg-white border border-slate-200 shadow-sm">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </Card>
  );
}

export default function ProduttoreDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [scadenzeCfg, setScadenzeCfg] = useState(null);
  const { zone: zoneAttive } = useZoneAttive();

  useEffect(() => {
    getProduttore(id).then(setData).catch(() => setData(null));
    getScadenze().then((r) => setScadenzeCfg(r.config)).catch(() => {});
  }, [id]);

  if (!data) return <div className="p-4 text-slate-500">Caricamento…</div>;

  const c = data.contratto || {};
  const pl = data.pianificazione || {};
  const days = daysUntil(c.dataScadenza);
  const cls = classifyScadenza(c.dataScadenza, scadenzeCfg);

  const setField = (key, value) => setData({ ...data, [key]: value });
  const setContratto = (key, value) => setData({ ...data, contratto: { ...c, [key]: value } });
  const setPian = (key, value) => setData({ ...data, pianificazione: { ...pl, [key]: value } });

  // Blocco A3 + B6.0: cambio frequenza con auto-clear / proposta di default
  const onChangeFrequenza = (newFreq) => {
    const next = { ...c, frequenza: newFreq };
    if (newFreq === "SETTIMANALE" || newFreq === "SU_APPUNTAMENTO") {
      next.alternanza = null;
      next.settimanaMese = null;
      next.meseAlternanza = null;
    } else if (newFreq === "BISETTIMANALE") {
      next.alternanza = c.alternanza || "DISPARI";
      next.settimanaMese = null;
      next.meseAlternanza = null;
    } else if (newFreq === "MENSILE") {
      next.settimanaMese = c.settimanaMese || "W1";
      next.alternanza = null;
      next.meseAlternanza = null;
    } else if (newFreq === "BIMESTRALE") {
      next.settimanaMese = c.settimanaMese || "W1";
      next.meseAlternanza = c.meseAlternanza || "PARI";
      next.alternanza = null;
    }
    setData({ ...data, contratto: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const fr = c.frequenza || "MENSILE";
      // Pianificazione: solo `giorno` resta sul produttore (A1). Mantengo legacy
      // alternanza_bisettimanale e occorrenza_mensile solo per retrocompat
      // ma il calendario (A2) ora legge dal contratto.
      const pian_save = { giorno: fr === "SU_APPUNTAMENTO" ? null : (pl.giorno || null) };

      const payload = {
        ragioneSociale: data.ragioneSociale,
        indirizzo: data.indirizzo,
        cap: data.cap || "",
        citta: data.citta,
        zona: data.zona,
        telefonoFisso: data.telefonoFisso,
        telefonoMobile: data.telefonoMobile,
        orarioRitiro: data.orarioRitiro,
        fasciaOrariaVisita: data.fasciaOrariaVisita || "SEMPRE_APERTO",
        partitaIva: data.partitaIva,
        latitudine: data.latitudine,
        longitudine: data.longitudine,
        note: data.note,
        attivo: data.attivo,
        contratto: {
          valorizzazione: c.valorizzazione ?? null,
          prezzoEuroKg: c.gratuito ? null : (c.prezzoEuroKg != null ? Number(c.prezzoEuroKg) : null),
          gratuito: !!c.gratuito,
          dataStipula: c.dataStipula || null,
          dataScadenza: c.dataScadenza || null,
          rinnovoTacito: c.rinnovoTacito || "NESSUNO",
          spurgo: c.spurgo != null && c.spurgo !== "" ? Number(c.spurgo) : null,
          frequenza: fr,
          // Nuovi campi A1+A2+B6.0
          alternanza: fr === "BISETTIMANALE" ? (c.alternanza || "DISPARI") : null,
          settimanaMese: (fr === "MENSILE" || fr === "BIMESTRALE") ? (c.settimanaMese || "W1") : null,
          meseAlternanza: fr === "BIMESTRALE" ? (c.meseAlternanza || "PARI") : null,
        },
        pianificazione: pian_save,
      };
      const updated = await updateProduttore(id, payload);
      setData(updated);
      toast.success("Produttore aggiornato");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const doGeocode = async () => {
    setGeocoding(true);
    try {
      const res = await geocodeSingle(id);
      if (res.lat != null) {
        setData({ ...data, latitudine: res.lat, longitudine: res.lng });
        toast.success("Coordinate trovate");
      } else {
        toast.error(res.error || "Nessun risultato");
      }
    } catch {
      toast.error("Errore geocoding");
    } finally {
      setGeocoding(false);
    }
  };

  const openOnMap = () => {
    if (data.latitudine && data.longitudine) {
      window.open(`https://www.google.com/maps?q=${data.latitudine},${data.longitudine}`, "_blank");
    } else if (data.indirizzo) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.indirizzo)}`, "_blank");
    }
  };

  const doDelete = async () => {
    if (!window.confirm("Eliminare questo produttore?")) return;
    await deleteProduttore(id);
    toast.success("Produttore eliminato");
    nav("/produttori");
  };

  return (
    <div className="space-y-4 pb-6" data-testid="produttore-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/produttori" className="flex items-center gap-1 text-slate-600 hover:text-slate-900 min-h-[44px]" data-testid="back-to-list">
          <ArrowLeft size={18} /> Torna alla lista
        </Link>
        <Button variant="outline" onClick={doDelete} data-testid="delete-producer-btn" className="text-[#C62828] border-[#C62828] hover:bg-[#C62828]/10 min-h-[44px]">
          <Trash2 size={16} className="mr-1" /> Elimina
        </Button>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Dettaglio produttore</div>
        <h1
          className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight break-words"
          style={{ fontFamily: "Outfit, sans-serif" }}
          data-testid="producer-title"
        >
          {data.ragioneSociale || "(senza nome)"}
        </h1>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {data.attivo === false && (
            <Badge data-testid="badge-disattivato" className="bg-slate-500 text-white">DISATTIVATO</Badge>
          )}
          {c.gratuito ? (
            <Badge className="bg-[#1B5E20]/10 text-[#1B5E20] hover:bg-[#1B5E20]/15">GRATUITO</Badge>
          ) : c.prezzoEuroKg != null ? (
            <Badge className="bg-[#1B5E20] text-white hover:bg-[#144818]">{Number(c.prezzoEuroKg).toFixed(2)} €/kg</Badge>
          ) : null}
          {c.spurgo ? <Badge className="bg-[#F57C00]/15 text-[#F57C00]">SPURGO {c.spurgo}</Badge> : null}
          {c.rinnovoTacito && c.rinnovoTacito !== "NESSUNO" && (
            <Badge variant="outline" className="border-slate-300 text-slate-600 text-[10px]">{c.rinnovoTacito}</Badge>
          )}
          {days != null && days < 0 && <Badge data-testid="badge-scaduto" className="bg-[#C62828] text-white">SCADUTO</Badge>}
          {cls === "rossa" && (
            <Badge data-testid="badge-rossa" style={{ backgroundColor: "transparent", color: SCADENZA_COLOR.rossa, border: `1.5px solid ${SCADENZA_COLOR.rossa}` }}>
              {SCADENZA_LABEL.rossa} {days}gg
            </Badge>
          )}
          {cls === "gialla" && (
            <Badge data-testid="badge-gialla" className="bg-[#F57C00] text-white">{SCADENZA_LABEL.gialla} {days}gg</Badge>
          )}
        </div>
      </div>

      <Button
        onClick={openOnMap}
        className="w-full h-12 bg-[#1B5E20] hover:bg-[#144818] text-white font-semibold"
        data-testid="open-on-map-btn"
      >
        <Map size={18} className="mr-2" /> APRI SU MAPPA
      </Button>

      <Section title="Anagrafica">
        <div className="flex items-center justify-between bg-slate-50 -mx-5 px-5 -mt-3 py-2 mb-2 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Label htmlFor="attivo-switch" className="font-semibold text-sm">Produttore attivo</Label>
            <span className="text-[11px] text-slate-500">{data.attivo ? "incluso nei giri" : "escluso dai giri"}</span>
          </div>
          <Switch
            id="attivo-switch"
            data-testid="attivo-switch"
            checked={data.attivo !== false}
            onCheckedChange={(v) => setField("attivo", v)}
          />
        </div>
        <div>
          <Label>Ragione sociale</Label>
          <Input
            data-testid="ragione-sociale-input"
            value={data.ragioneSociale || ""}
            onChange={(e) => setField("ragioneSociale", e.target.value)}
          />
        </div>
        <div>
          <Label>Indirizzo</Label>
          <Input data-testid="indirizzo-input" value={data.indirizzo || ""} onChange={(e) => setField("indirizzo", e.target.value)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label>Città</Label>
            <Input
              data-testid="citta-input"
              value={data.citta || ""}
              onChange={(e) => setField("citta", e.target.value)}
              placeholder={data.cittaDedotta && data.cittaEffettiva ? `Dedotta: ${data.cittaEffettiva}` : ""}
            />
            {data.cittaDedotta && data.cittaEffettiva && !data.citta && (
              <div
                className="text-[11px] text-[#1565C0] bg-[#1565C0]/10 border border-[#1565C0]/30 rounded-md px-2 py-1 mt-1 flex items-center gap-2"
                data-testid="citta-dedotta-hint"
              >
                <span>
                  📍 Città dedotta dall&apos;indirizzo: <span className="font-semibold">{data.cittaEffettiva}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setField("citta", data.cittaEffettiva)}
                  className="ml-auto text-[11px] font-semibold text-[#1565C0] hover:underline"
                  data-testid="citta-dedotta-confirm-btn"
                >
                  Usa questa città
                </button>
              </div>
            )}
            {!data.citta && !data.cittaEffettiva && (
              <div
                className="text-[11px] text-slate-500 mt-1"
                data-testid="citta-vuota-hint"
              >
                Campo vuoto: nessuna città deducibile dall&apos;indirizzo. Inserisci manualmente.
              </div>
            )}
          </div>
          <div>
            <Label>CAP</Label>
            <Input
              data-testid="cap-input"
              value={data.cap || ""}
              onChange={(e) => setField("cap", e.target.value)}
              maxLength={5}
              inputMode="numeric"
              pattern="\d{5}"
              placeholder="50121"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Zona</Label>
            <Select value={data.zona || ""} onValueChange={(v) => setField("zona", v)}>
              <SelectTrigger data-testid="zona-select"><SelectValue placeholder="Seleziona zona" /></SelectTrigger>
              <SelectContent>
                {zoneAttive.map((z) => (
                  <SelectItem key={z.id} value={z.nome}>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: z.colore }} />
                      {z.nome}
                    </span>
                  </SelectItem>
                ))}
                {/* Mantieni il valore corrente se la zona è disattivata o non più presente */}
                {data.zona && !zoneAttive.some((z) => z.nome === data.zona) && (
                  <SelectItem value={data.zona}>
                    <span className="inline-flex items-center gap-2 text-slate-500 italic">
                      {data.zona} (zona non attiva)
                    </span>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {data.zona && !zoneAttive.some((z) => z.nome === data.zona) && (
              <div className="text-[11px] text-[#F57C00] mt-1" data-testid="zona-non-attiva-warning">
                Questa zona è disattivata o non più presente. Seleziona una zona attiva per aggiornare.
              </div>
            )}
          </div>
          <div>
            <Label>Orario ritiro</Label>
            <Input data-testid="orario-input" value={data.orarioRitiro || ""} onChange={(e) => setField("orarioRitiro", e.target.value)} />
            <div className="text-[10px] text-slate-400 mt-0.5">
              Testo libero. Es. <span className="font-mono">8,00-12,00</span>, <span className="font-mono">mattina</span>, <span className="font-mono">su appuntamento</span>.
            </div>
          </div>
          <div>
            <Label>Orario visita</Label>
            <Select
              value={data.fasciaOrariaVisita || "DA_VERIFICARE"}
              onValueChange={(v) => setField("fasciaOrariaVisita", v)}
            >
              <SelectTrigger data-testid="fascia-visita-select">
                <SelectValue placeholder="Seleziona fascia" />
              </SelectTrigger>
              <SelectContent>
                {FASCE_VISITA.map((f) => (
                  <SelectItem key={f.v} value={f.v} data-testid={`fascia-visita-opt-${f.v}`}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1">
              L&apos;orario visita determina l&apos;ordine operativo del giro. Se non è noto, lasciare <span className="font-semibold">Orario da verificare</span>.
            </div>
            {data.fasciaOrariaVisitaSospetta && data.fasciaOrariaVisita === "SEMPRE_APERTO" && (
              <div
                className="text-[11px] text-[#F57C00] bg-[#F57C00]/10 border border-[#F57C00]/30 rounded-md px-2 py-1 mt-1"
                data-testid="fascia-visita-sospetta-warning"
              >
                ⚠ Dato importato da verificare: l&apos;orario non era presente nel file originale.
                Salvando un valore valido aggiornerai il record.
              </div>
            )}
          </div>
          <div>
            <Label>Telefono fisso</Label>
            <Input data-testid="tel-fisso-input" value={data.telefonoFisso || ""} onChange={(e) => setField("telefonoFisso", e.target.value)} />
          </div>
          <div>
            <Label>Telefono mobile</Label>
            <Input data-testid="tel-mobile-input" value={data.telefonoMobile || ""} onChange={(e) => setField("telefonoMobile", e.target.value)} />
          </div>
          <div>
            <Label>Partita IVA</Label>
            <Input data-testid="piva-input" value={data.partitaIva || ""} onChange={(e) => setField("partitaIva", e.target.value)} />
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label>Latitudine</Label>
            <Input data-testid="lat-input" type="number" step="any" value={data.latitudine ?? ""} onChange={(e) => setField("latitudine", e.target.value ? Number(e.target.value) : null)} />
          </div>
          <div className="flex-1">
            <Label>Longitudine</Label>
            <Input data-testid="lng-input" type="number" step="any" value={data.longitudine ?? ""} onChange={(e) => setField("longitudine", e.target.value ? Number(e.target.value) : null)} />
          </div>
          <Button variant="outline" onClick={doGeocode} disabled={geocoding} data-testid="auto-geocode-btn" className="min-h-[44px]">
            {geocoding ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />} Auto
          </Button>
        </div>
      </Section>

      <Section title="Contratto">
        <div className="flex items-center justify-between">
          <Label htmlFor="gratuito-switch" className="flex items-center gap-2">Gratuito</Label>
          <Switch id="gratuito-switch" data-testid="gratuito-switch" checked={!!c.gratuito} onCheckedChange={(v) => setContratto("gratuito", v)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Prezzo €/kg</Label>
            <Input data-testid="prezzo-input" type="number" step="0.01" value={c.prezzoEuroKg ?? ""} disabled={!!c.gratuito} onChange={(e) => setContratto("prezzoEuroKg", e.target.value)} />
          </div>
          <div>
            <Label>Valorizzazione</Label>
            <Input data-testid="valorizzazione-input" type="number" step="0.01" value={c.valorizzazione ?? ""} onChange={(e) => setContratto("valorizzazione", e.target.value ? Number(e.target.value) : null)} />
          </div>
          <div>
            <Label>Spurgo</Label>
            <Input data-testid="spurgo-input" type="number" step="0.1" value={c.spurgo ?? ""} onChange={(e) => setContratto("spurgo", e.target.value)} />
          </div>
          <div>
            <Label>Frequenza</Label>
            <Select value={c.frequenza || "MENSILE"} onValueChange={onChangeFrequenza}>
              <SelectTrigger data-testid="freq-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FREQUENZE.map((f) => <SelectItem key={f} value={f}>{FREQUENZE_LABEL[f] || f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Blocco A3: campi dinamici dipendenti dalla frequenza */}
          {c.frequenza === "BISETTIMANALE" && (
            <div data-testid="alternanza-wrap">
              <Label>Alternanza</Label>
              <Select value={c.alternanza || "DISPARI"} onValueChange={(v) => setContratto("alternanza", v)}>
                <SelectTrigger data-testid="alternanza-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALTERNANZE.map((a) => (
                    <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {c.frequenza === "MENSILE" && (
            <div data-testid="settimana-mese-wrap">
              <Label>Settimana del mese</Label>
              <Select value={c.settimanaMese || "W1"} onValueChange={(v) => setContratto("settimanaMese", v)}>
                <SelectTrigger data-testid="settimana-mese-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SETTIMANE_MESE.map((s) => (
                    <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {c.frequenza === "BIMESTRALE" && (
            <>
              <div data-testid="settimana-mese-wrap-bimestrale">
                <Label>Settimana del mese</Label>
                <Select value={c.settimanaMese || "W1"} onValueChange={(v) => setContratto("settimanaMese", v)}>
                  <SelectTrigger data-testid="settimana-mese-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SETTIMANE_MESE.map((s) => (
                      <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div data-testid="mese-alternanza-wrap">
                <Label>Mese alternanza</Label>
                <Select value={c.meseAlternanza || "PARI"} onValueChange={(v) => setContratto("meseAlternanza", v)}>
                  <SelectTrigger data-testid="mese-alternanza-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MESI_ALTERNANZA.map((m) => (
                      <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div>
            <Label>Data stipula</Label>
            <Input data-testid="data-stipula-input" type="date" value={c.dataStipula || ""} onChange={(e) => setContratto("dataStipula", e.target.value)} />
          </div>
          <div>
            <Label>Data scadenza</Label>
            <Input data-testid="data-scadenza-input" type="date" value={c.dataScadenza || ""} onChange={(e) => setContratto("dataScadenza", e.target.value)} />
          </div>
          <div>
            <Label>Rinnovo tacito</Label>
            <Select value={c.rinnovoTacito || "NESSUNO"} onValueChange={(v) => setContratto("rinnovoTacito", v)}>
              <SelectTrigger data-testid="rinnovo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RINNOVI.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-xs text-slate-500">Scadenza: {formatDateIt(c.dataScadenza)}</div>
      </Section>

      <Section title="Pianificazione">
        <PianificazioneSection c={c} pl={pl} setPian={setPian} />
      </Section>

      <Section title="Note">
        <Textarea data-testid="note-input" value={data.note || ""} onChange={(e) => setField("note", e.target.value)} rows={3} />
      </Section>

      <Button onClick={save} disabled={saving} data-testid="save-producer-btn" className="w-full h-12 bg-[#1B5E20] hover:bg-[#144818] text-white font-semibold">
        {saving ? <Loader2 size={18} className="animate-spin mr-2" /> : <Save size={18} className="mr-2" />} SALVA MODIFICHE
      </Button>

      <Button asChild variant="outline" className="w-full h-12 min-h-[44px]" data-testid="open-storico-btn">
        <Link to={`/storico-cliente?pid=${id}`}>
          <Clock size={16} className="mr-2" /> Vedi storico ritiri di questo cliente
        </Link>
      </Button>
    </div>
  );
}



// Blocco A3: Pianificazione semplificata — solo Giorno (la settimana fissa
// è stata rimossa dal produttore; alternanza/settimanaMese vivono nel contratto).
function PianificazioneSection({ c, pl, setPian }) {
  const freq = c.frequenza || "SETTIMANALE";

  if (freq === "SU_APPUNTAMENTO") {
    return (
      <div className="sm:col-span-2" data-testid="pianificazione-section">
        <p className="text-[11px] text-slate-500">
          Nessuna pianificazione automatica. Il produttore va contattato per fissare il ritiro.
        </p>
      </div>
    );
  }

  let helper = "Il produttore sarà visitato ogni settimana nel giorno selezionato.";
  if (freq === "BISETTIMANALE") {
    const alt = c.alternanza || "DISPARI";
    helper = alt === "DISPARI"
      ? "Visite due volte al mese, settimane DISPARI (1ª e 3ª)."
      : "Visite due volte al mese, settimane PARI (2ª e 4ª).";
  } else if (freq === "MENSILE") {
    const sm = c.settimanaMese || "W1";
    helper = `Visita mensile nella settimana ${sm} del mese.`;
  } else if (freq === "BIMESTRALE") {
    const sm = c.settimanaMese || "W1";
    const ma = c.meseAlternanza || "PARI";
    const mesi = ma === "PARI" ? "(feb, apr, giu, ago, ott, dic)" : "(gen, mar, mag, lug, set, nov)";
    helper = `Visita ogni 2 mesi nei mesi ${ma.toLowerCase()} ${mesi}, settimana ${sm}.`;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="pianificazione-section">
      <div>
        <Label>Giorno della settimana</Label>
        <Select value={pl.giorno || ""} onValueChange={(v) => setPian("giorno", v || null)}>
          <SelectTrigger data-testid="giorno-select"><SelectValue placeholder="-" /></SelectTrigger>
          <SelectContent>
            {GIORNI.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-500 mt-1">{helper}</p>
      </div>
    </div>
  );
}
